import { useEffect, useRef } from 'react';

import { createSelector } from '@reduxjs/toolkit';

import { useAppSelector } from '../../app/hooks';
import { selectCurrentIdentity, selectCurrentPermissions, selectCurrentRoles } from './authSlice';
import type { IIdentity } from './types/Identity';
import type { Permissions } from './types/Permissions';
import type { CurrentRoleSet, RoleSet } from './types/Roles';
import { HasRoleOp } from './types/Roles';

export const usePermissions = (): Permissions => useAppSelector(selectCurrentPermissions);

const useRolesSelector = createSelector(selectCurrentRoles, (roles: CurrentRoleSet) => ({
  roles,
  hasRole: (requiredRole: RoleSet, op = HasRoleOp.Any): boolean => {
    const check = roles & requiredRole;
    if (op === HasRoleOp.Any) {
      // Any bit is good for us.
      return check !== 0;
    } else {
      // All bits must match.
      return check === requiredRole;
    }
  },
}));

export const useRoles = () => useAppSelector(useRolesSelector);

export const useIdentity = (): IIdentity => useAppSelector(selectCurrentIdentity);

export const useExactlyOnce = (callback: () => void) => {
  const isTriggered = useRef(false);

  useEffect(() => {
    if (!isTriggered.current) {
      callback();
    }
    return () => {
      isTriggered.current = true;
    };
  }, [callback]);
};

export { Permission } from './types/Permissions';

export { Role } from './types/Roles';
